/**
 * store/authStore.ts - 认证状态管理（Zustand）
 *
 * 功能说明：
 * - 用户认证状态管理
 * - 登录、注册、注销
 * - Token 管理
 * - 权限检查
 *
 * 依赖关系：
 * - 依赖 zustand
 * - 依赖 store/auth/types.ts
 * - 依赖 store/auth/permissions.ts
 * - 依赖 store/auth/token.ts
 *
 * 关键注释索引：
 * - [Zustand Store] - 见下方 create 函数
 *
 * 创建日期：2026-05-03
 * 作者：AI Assistant
 * 重构日期：2026-05-03 - 拆分为 auth 模块
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { UserInfo, LoginCredentials, RegisterData, AuthState } from './auth/types';
import { checkPermission, hasRole, isPermissionsExpired } from './auth/permissions';
import { API_BASE_URL, getTokenFromStorage } from './auth/token';

// 权限刷新请求的 Promise（防止并发刷新）
let permissionsRefreshPromise: Promise<void> | null = null;

// 创建认证 store
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // ==================== 初始状态 ====================
      isAuthenticated: false,
      user: null,
      token: null,
      loading: false,
      error: null,

      // ==================== 登录 ====================
      login: async (credentials: LoginCredentials) => {
        set({ loading: true, error: null });

        try {
          logger.info(`[AuthStore] 开始登录: ${credentials.username}`);

          const response = await fetch(`${API_BASE_URL}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(credentials),
          });

          const data = await response.json();

          logger.debug('[AuthStore] 登录响应', data);

          if (data.success) {
            // 确保 permissions 数组存在
            const userData: UserInfo = {
              ...data.user,
              permissions: data.user?.permissions || [],
              permissionsUpdatedAt: data.user?.permissionsUpdatedAt
                ? new Date(data.user.permissionsUpdatedAt).getTime()
                : Date.now()
            };

            set({
              isAuthenticated: true,
              user: userData,
              token: data.token,
              loading: false,
              error: null,
            });

            return { success: true, message: data.message };
          } else {
            set({ loading: false, error: data.message });
            return { success: false, message: data.message };
          }
        } catch (error) {
          const errorMessage = '网络错误，请检查连接';
          set({ loading: false, error: errorMessage });
          return { success: false, message: errorMessage };
        }
      },

      // ==================== 注册 ====================
      register: async (userData: RegisterData) => {
        set({ loading: true, error: null });

        try {
          const response = await fetch(`${API_BASE_URL}/auth/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(userData),
          });

          const data = await response.json();

          if (data.success) {
            set({
              isAuthenticated: true,
              user: data.user,
              token: data.token,
              loading: false,
              error: null,
            });
            return { success: true, message: data.message };
          } else {
            set({ loading: false, error: data.message });
            return { success: false, message: data.message };
          }
        } catch (error) {
          const errorMessage = '网络错误，请检查连接';
          set({ loading: false, error: errorMessage });
          return { success: false, message: errorMessage };
        }
      },

      // ==================== 注销 ====================
      logout: () => {
        const { user, token } = get();

        // 异步发送注销请求到后端（不阻塞）
        if (user && token) {
          fetch(`${API_BASE_URL}/auth/logout`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ userId: user.id, username: user.username }),
          }).catch(error => console.error('注销请求失败:', error));
        }

        // 清除本地状态
        set({
          isAuthenticated: false,
          user: null,
          token: null,
          loading: false,
          error: null,
        });
      },

      // ==================== Token 刷新 ====================
      refreshToken: async () => {
        const { token } = get();

        if (!token) return false;

        try {
          const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken: token }),
          });

          const data = await response.json();

          if (data.success) {
            set({ token: data.token });
            return true;
          } else {
            set({ isAuthenticated: false, user: null, token: null });
            return false;
          }
        } catch (error) {
          console.error('刷新令牌失败:', error);
          set({ isAuthenticated: false, user: null, token: null });
          return false;
        }
      },

      // ==================== 用户信息更新 ====================
      updateUserInfo: (userInfo) => {
        const { user } = get();
        if (user) {
          set({ user: { ...user, ...userInfo } });
        }
      },

      // ==================== 错误处理 ====================
      clearError: () => set({ error: null }),
      setLoading: (loading) => set({ loading }),

      // ==================== 权限检查 ====================
      checkPermission: (permission: string) => {
        return checkPermission(get().user, permission);
      },

      hasRole: (role: string) => {
        return hasRole(get().user, role);
      },

      isPermissionsExpired: () => {
        return isPermissionsExpired(get().user);
      },

      // ==================== 权限刷新 ====================
      refreshUserPermissions: async () => {
        // 防止并发刷新
        if (permissionsRefreshPromise) {
          return permissionsRefreshPromise;
        }

        permissionsRefreshPromise = (async () => {
          const { token, user, logout } = get();

          if (!token || !user) {
            logger.info('[AuthStore] 无法刷新权限：未登录');
            return;
          }

          try {
            logger.info('[AuthStore] 开始刷新用户权限...');

            const response = await fetch(`${API_BASE_URL}/auth/me`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
            });

            if (response.status === 401) {
              logger.warn('[AuthStore] 权限刷新返回 401，执行退出登录');
              logout();
              return;
            }

            const data = await response.json();

            if (response.ok && data.success && data.user) {
              logger.info('[AuthStore] 权限刷新成功');

              const latestUser = get().user;
              if (!latestUser) return;

              set({
                user: {
                  ...latestUser,
                  ...data.user,
                  permissions: data.user.permissions || [],
                  permissionsUpdatedAt: data.user.permissionsUpdatedAt
                    ? new Date(data.user.permissionsUpdatedAt).getTime()
                    : Date.now()
                }
              });
            } else {
              console.error('[AuthStore] 权限刷新失败:', data.message);
            }
          } catch (error) {
            console.error('[AuthStore] 刷新权限时发生错误:', error);
          } finally {
            permissionsRefreshPromise = null;
          }
        })();

        return permissionsRefreshPromise;
      },
    }),
    {
      name: 'auth-storage',
      // 只持久化认证相关状态
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        token: state.token,
      }),
    }
  )
);

// 重新导出类型供外部使用
export type { UserInfo, LoginCredentials, RegisterData, AuthState } from './auth/types';
import { logger } from '@/utils/logger';
